import { Theme, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import CancelIcon from '@mui/icons-material/Cancel';
import { useEffect, useRef, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import "./MultipleSelectionChip.css";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

interface Options {
    defaultSelection: string[];
    handleSelection?: (value: string[])=> void;
    name?: string;
    values: string[];
    sx?: any;
    options?: any;
    limitValue: Number;
    cardsCount?: number;
}

function getStyles(name: string, option: readonly string[], theme: Theme) {
    return {
        fontWeight:
            option.indexOf(name) === -1
                ? theme.typography.fontWeightRegular
                : theme.typography.fontWeightMedium,
    };
}

function MultipleSelectChip(props: Options): JSX.Element {
    const theme = useTheme();
    const {defaultSelection, handleSelection, options, values, limitValue, cardsCount} = props;
    const[selectedOption, setSelectedOption] = useState<string[]>(defaultSelection);
    const isDefaultUpdate = useRef(false);

    useEffect(() => {
        if (isDefaultUpdate.current) {
            isDefaultUpdate.current = false;
            return;
        }
        if(defaultSelection.join(',') != selectedOption.join(',')){
            handleSelection?.(selectedOption);
        }
    }, [selectedOption]);

    useEffect(() => {
        if (defaultSelection.join(',') !== selectedOption.join(',')) {
            isDefaultUpdate.current = true;
            setSelectedOption(defaultSelection);
        }
    }, [defaultSelection]);

    const handleChange = (event: any) => {
        const {
            target: { value },
        } = event;
        setSelectedOption(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    const handleDelete = (e: React.MouseEvent, value: string) => {
        e.preventDefault();
        setSelectedOption(selectedOption.filter((op: any) => op !== value));
    };

    return (
        <FormControl sx={props.sx} size="small" className="cardSelect">
            <InputLabel id="multiple-chip-label">{props.name}</InputLabel>
            <Select
                labelId="multiple-chip-label"
                id="multiple-chip"
                multiple
                value={selectedOption}
                onChange={handleChange}
                input={<OutlinedInput id="select-multiple-chip" label={props.name} />}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                        },
                    },
                    disableEnforceFocus: true,
                }}
                renderValue={(selected) => {
                    const limit:any = limitValue;
                    const visibleTags = selected.slice(0, limit);
                    const hiddenCount = selected.length - limit;

                    const tooltipTitle = selected
                        .map((value) => (options?.label?.format ? options.label.format(value) : value))
                        .join(', ');

                    return (
                        <Tooltip title={tooltipTitle || ''} arrow>
                            <Box sx={{ display: 'flex', alignItems: 'center', columnGap:'3px', flexWrap: 'wrap', gap: 0.5, maxHeight: '24px', overflowY: 'auto' }}>
                                {visibleTags.map((value) => (
                                    <Chip
                                        key={value}
                                        label={options?.label?.format ? options.label.format(value) : value}
                                        clickable
                                        deleteIcon={<CancelIcon onMouseDown={(event) => event.stopPropagation()} />}
                                        onDelete={(e) => handleDelete(e, value)}
                                        size="small"
                                    />
                                ))}
                                {hiddenCount > 0 && (
                                    <Chip label={`+${hiddenCount}`} size="small" sx={{ backgroundColor: '#e0e0e0' }} />
                                )}
                            </Box>
                        </Tooltip>
                    );
                }}
            >
                {props.values.map((name) => (
                    <MenuItem
                        key={name}
                        value={name}
                        style={getStyles(name, selectedOption, theme)}
                        disabled={selectedOption.length >= Number(cardsCount) && !selectedOption.includes(name)} // Disable options if max limit is reached
                    >
                        {options?.label?.format ? options.label.format(name) : name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

export default MultipleSelectChip;